import { clsx } from 'clsx';
import { NATURAL_TERRAINS, getNaturalExplorerCount, getNaturalTerrain } from '@character-creator/data';

interface NaturalExplorerSelectorProps {
  rangerLevel: number;
  selectedTerrains: string[];
  onToggle: (terrainId: string) => void;
  compact?: boolean;
}

export function NaturalExplorerSelector({
  rangerLevel,
  selectedTerrains,
  onToggle,
  compact = false,
}: NaturalExplorerSelectorProps) {
  const allowedCount = getNaturalExplorerCount(rangerLevel);
  const selectedCount = selectedTerrains.length;
  const needsMore = selectedCount < allowedCount;

  // Get selected terrain names for compact display
  const selectedNames = selectedTerrains
    .map((id) => getNaturalTerrain(id)?.name)
    .filter(Boolean);

  // Compact view - shows selected terrains
  if (compact && !needsMore && selectedNames.length > 0) {
    return (
      <div className="text-xs text-success">
        <span>Natural Explorer: {selectedNames.join(', ')}</span>
      </div>
    );
  }

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Natural Explorer
        {needsMore && (
          <span className="text-warning ml-2 text-xs">
            (Choose {allowedCount - selectedCount} more)
          </span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        You are adept at traveling and surviving in certain types of terrain. You gain benefits
        when traveling and foraging in your favored terrain.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {NATURAL_TERRAINS.map((terrain) => {
          const isSelected = selectedTerrains.includes(terrain.id);
          const isDisabled = !isSelected && selectedCount >= allowedCount;

          return (
            <button
              key={terrain.id}
              type="button"
              onClick={() => onToggle(terrain.id)}
              disabled={isDisabled}
              className={clsx(
                'w-full text-left p-2 rounded-lg border transition-all duration-200',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : isDisabled
                    ? 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                    : 'bg-background-secondary border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="font-medium text-text-primary text-sm">{terrain.name}</div>
              <p className="text-xs text-text-muted mt-0.5">{terrain.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
